import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, DataTable } from '@w7/ui'
import { bookmakerApi, bmErrorMessage, unwrapBmList } from '@/api/bookmaker'
import { getLabAdminLevel } from '@/config/lab'
import { BmPage } from './BmPage'
import {
  marketListEditId,
  marketListEventName,
  marketListMarketId,
  marketListName,
  marketListRowKey,
  marketListStatus,
} from './marketListDisplay'

export function InPlayPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [pageNo, setPageNo] = useState(1)
  const [totalItems, setTotalItems] = useState(0)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  const load = useCallback(async (page: number) => {
    setBusy(true)
    setError(null)
    try {
      const admin = getLabAdminLevel()
      const data =
        admin === 'Horse Racing'
          ? await bookmakerApi.marketHorsePagination({ pageNo: page })
          : await bookmakerApi.marketList({ pageNo: page })
      const list = unwrapBmList(data)
      setRows(list)
      if (data && typeof data === 'object') {
        const o = data as Record<string, unknown>
        if (typeof o.totalItems === 'number') setTotalItems(o.totalItems)
        if (typeof o.itemsPerPage === 'number') setItemsPerPage(o.itemsPerPage)
      }
      setPageNo(page)
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }, [])

  useEffect(() => {
    void load(1)
  }, [load])

  const totalPages = Math.max(1, Math.ceil((totalItems || rows.length) / (itemsPerPage || 10)))

  return (
    <BmPage
      title="In Play"
      description="Lab /InPlay — open Markets View from the eye action"
      error={error}
      // Market Selection temporarily disabled
      // actions={
      //   <Link to="/marketSelect">
      //     <Button variant="secondary">Back to Market Selection</Button>
      //   </Link>
      // }
    >
      <Card>
        <DataTable
          label="In Play markets"
          columns={[
            {
              id: 'sr',
              header: 'Sr. No',
              cell: (_r, i) => String(itemsPerPage * (pageNo - 1) + (i ?? 0) + 1),
            },
            {
              id: 'marketId',
              header: 'Market ID',
              cell: (r: Record<string, unknown>) => marketListMarketId(r),
            },
            {
              id: 'name',
              header: 'Name',
              cell: (r: Record<string, unknown>) => marketListName(r),
            },
            {
              id: 'event',
              header: 'Event Name',
              cell: (r: Record<string, unknown>) => marketListEventName(r),
            },
            {
              id: 'status',
              header: 'Status',
              cell: (r: Record<string, unknown>) => marketListStatus(r),
            },
            {
              id: 'action',
              header: 'Action',
              cell: (r: Record<string, unknown>) => {
                const mid = marketListEditId(r)
                const jodi = Boolean(r.isJodiMarket)
                return (
                  <Button
                    variant="secondary"
                    disabled={!mid}
                    onClick={() => navigate(jodi ? `/jodimarketsview/${mid}` : `/marketsview/${mid}`)}
                  >
                    View
                  </Button>
                )
              },
            },
          ]}
          rows={rows}
          loading={busy}
          rowKey={(r) => marketListRowKey(r) || String(Math.random())}
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <Button
            variant="secondary"
            disabled={busy || pageNo <= 1}
            onClick={() => void load(pageNo - 1)}
          >
            Prev
          </Button>
          <span>
            Page {pageNo} / {totalPages}
          </span>
          <Button
            variant="secondary"
            disabled={busy || pageNo >= totalPages}
            onClick={() => void load(pageNo + 1)}
          >
            Next
          </Button>
        </div>
      </Card>
    </BmPage>
  )
}
